import React, { useEffect } from "react";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { useNavigation } from "@react-navigation/native";
import { useColors } from "../hooks/useColors";
import { useTutorial } from "../hooks/useTutorial";
import { BuyerOrder } from "../screens/BuyerOrderDetailsScreen";
import { logger } from "../utils/logger";

import MainTabNavigator from "./MainTabNavigator";
import CartScreen from "../screens/CartScreen";
import VendorScreen from "../screens/VendorScreen";
import VendorsScreen from "../screens/VendorsScreen";
import VendorSettingsScreen from "../screens/VendorSettings";
import VendorRegistrationScreen from "../screens/VendorRegistration";
import ProductDetailsScreen from "../screens/ProductDetails";
import AddProductScreen from "../screens/AddProductScreen";
import { ChatScreen } from "../screens/ChatScreen";
import { ArchivedChatsScreen } from "../screens/ArchivedChatsScreen";
import LikesScreen from "../screens/LikesScreen";
import AboutScreen from "../screens/AboutScreen";
import TutorialScreen from "../screens/TutorialScreen";
import SupportScreen from "../screens/SupportScreen";
import NotificationsScreen from "../screens/NotificationsScreen";
import DeliveryAddressScreen from "../screens/DeliveryAddressScreen";
import OrdersScreen from "../screens/OrdersScreen";
import PaymentMethodsScreen from "../screens/PaymentMethodsScreen";
import CheckoutScreen from "../screens/CheckoutScreen";
import OrderTrackingScreen from "../screens/OrderTrackingScreen";
import ProfileSetupScreen from "../screens/ProfileSetupScreen";
import PrivacyPolicyScreen from "../screens/PrivacyPolicyScreen";
import TermsAndConditionsScreen from "../screens/TermsAndConditions";
import CartsByStoreScreen from "../screens/CartsByStoreScreen";
import VendorOrderDetailsScreen from "../screens/VendorOrderDetailsScreen";
import BuyerOrderDetailsScreen from "../screens/BuyerOrderDetailsScreen";
import ReceiptScreen from "../screens/ReceiptScreen";
import PaymentProcessingInfoScreen from "../screens/PaymentProcessingInfoScreen";
import DeleteAccountScreen from "../screens/DeleteAccountScreen";
import NotificationSettingsScreen from "../screens/NotificationSettingsScreen";
import PasswordResetScreen from "../screens/Auth/PasswordReset";

export type MainStackParamList = {
  Main: undefined;
  Cart: { vendorId?: string } | undefined;
  Vendor: undefined;
  Vendors: undefined;
  VendorSettings: undefined;
  VendorRegistration: undefined;
  ProductDetails: undefined;
  AddProduct: undefined;
  Chat: { id: string };
  ArchivedChats: undefined;
  Likes: undefined;
  About: undefined;
  Tutorial: undefined;
  Support: undefined;
  Notifications: undefined;
  DeliveryAddress: undefined;
  Orders: { initialTab?: "purchases" | "pending" } | undefined;
  PaymentMethods: undefined;
  Checkout: undefined;
  OrderTracking: undefined;
  ProfileSetup: undefined;
  Home: undefined;
  PrivacyPolicy: undefined;
  TermsAndConditions: undefined;
  CartsByStore: undefined;
  VendorOrderDetails: { vendorOrderId: string };
  BuyerOrderDetails: { order: BuyerOrder };
  Receipt: { order: BuyerOrder };
  PaymentProcessingInfo: undefined;
  DeleteAccount: undefined;
  NotificationSettings: undefined;
  PasswordReset: undefined;
};

const Stack = createNativeStackNavigator<MainStackParamList>();

// Wrapper component for MainTabNavigator that checks tutorial status
function MainTabNavigatorWrapper() {
  const navigation = useNavigation();
  const { shouldShowTutorial, isChecking } = useTutorial();
  const hasNavigated = React.useRef(false);

  useEffect(() => {
    // Only navigate to tutorial once, when we're done checking and tutorial should be shown
    if (!isChecking && shouldShowTutorial && !hasNavigated.current) {
      hasNavigated.current = true;
      logger.debug('Navigation', 'Navigating to Tutorial', { isFirstTime: true });
      // Small delay to ensure navigation is ready
      setTimeout(() => {
        navigation.navigate('Tutorial' as never, { isFirstTime: true } as never);
      }, 100);
    }
  }, [isChecking, shouldShowTutorial, navigation]);

  return <MainTabNavigator />;
}

export default function MainStackNavigator() {
  const colors = useColors();

  return (
    <Stack.Navigator
      screenOptions={{
        headerBackTitleStyle: { fontFamily: undefined, fontSize: undefined },
        headerTintColor: colors.primary,
        headerTitleStyle: {
          fontFamily: undefined,
          fontSize: undefined,
          fontWeight: '600',
          color: '#FFFFFF', // Force white for dark mode
        },
        headerStyle: {
          backgroundColor: colors.headerBackground,
        },
        contentStyle: {
          backgroundColor: colors.background,
        },
        // Smooth iOS-style slide animation for all screens
        animation: 'slide_from_right',
        // Ensure gesture-based back navigation shows dark background
        gestureEnabled: true,
      }}
    >
      <Stack.Screen
        name="Main"
        component={MainTabNavigatorWrapper}
        options={{ headerShown: false }}
      />
      <Stack.Screen name="Cart" component={CartScreen} />
      <Stack.Screen name="Vendor" component={VendorScreen} />
      <Stack.Screen name="Vendors" component={VendorsScreen} />
      <Stack.Screen
        name="VendorSettings"
        component={VendorSettingsScreen}
        options={{ title: "Store Settings" }}
      />
      <Stack.Screen
        name="VendorRegistration"
        component={VendorRegistrationScreen}
        options={{ title: "Create Store" }}
      />
      <Stack.Screen
        name="ProductDetails"
        component={ProductDetailsScreen as any}
        options={{ title: "Product Details" }}
      />
      <Stack.Screen
        name="AddProduct"
        component={AddProductScreen}
        options={{ title: "Add Product" }}
      />
      <Stack.Screen
        name="Chat"
        component={ChatScreen}
        options={{ title: "Chat" }}
      />
      <Stack.Screen
        name="ArchivedChats"
        component={ArchivedChatsScreen}
        options={{ title: "Archived Chats" }}
      />
      <Stack.Screen
        name="Likes"
        component={LikesScreen}
        options={{ title: "Liked Products" }}
      />
      <Stack.Screen
        name="About"
        component={AboutScreen}
        options={{ title: "About JadeTrade" }}
      />
      <Stack.Screen
        name="Tutorial"
        component={TutorialScreen}
        options={{ title: "Tutorial", headerShown: false }}
      />
      <Stack.Screen
        name="Support"
        component={SupportScreen}
        options={{ title: "Support" }}
      />
      <Stack.Screen
        name="Notifications"
        component={NotificationsScreen}
        options={{ title: "Notifications", headerShown: false }}
      />
      <Stack.Screen
        name="DeliveryAddress"
        component={DeliveryAddressScreen}
        options={{ title: "Deliver to Me" }}
      />
      <Stack.Screen
        name="Orders"
        component={OrdersScreen}
        options={{ title: "Orders" }}
      />
      <Stack.Screen
        name="PaymentMethods"
        component={PaymentMethodsScreen}
        options={{ title: "Payment Methods" }}
      />
      <Stack.Screen
        name="Checkout"
        component={CheckoutScreen}
        options={{
          title: "Checkout",
        }}
      />
      <Stack.Screen
        name="OrderTracking"
        component={OrderTrackingScreen}
        options={{ title: "Order Tracking" }}
      />
      <Stack.Screen
        name="ProfileSetup"
        component={ProfileSetupScreen}
        options={{
          title: "Create Username",
          headerShown: false,
        }}
      />
      <Stack.Screen
        name="PrivacyPolicy"
        component={PrivacyPolicyScreen}
        options={{ title: "Privacy Policy" }}
      />
      <Stack.Screen
        name="TermsAndConditions"
        component={TermsAndConditionsScreen}
        options={{ title: "Terms and Conditions" }}
      />
      <Stack.Screen
        name="CartsByStore"
        component={CartsByStoreScreen}
        options={{ title: "" }}
      />
      <Stack.Screen
        name="VendorOrderDetails"
        component={VendorOrderDetailsScreen}
        options={{ title: "Order Details" }}
      />
      <Stack.Screen
        name="BuyerOrderDetails"
        component={BuyerOrderDetailsScreen}
        options={{ title: "Order Details" }}
      />
      <Stack.Screen
        name="Receipt"
        component={ReceiptScreen}
        options={{ title: "Receipt" }}
      />
      <Stack.Screen
        name="PaymentProcessingInfo"
        component={PaymentProcessingInfoScreen}
        // options={{ title: "Payment Processing Info" }}
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="DeleteAccount"
        component={DeleteAccountScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="NotificationSettings"
        component={NotificationSettingsScreen}
        options={{ title: "Notification Settings" }}
      />
      <Stack.Screen
        name="PasswordReset"
        component={PasswordResetScreen}
        options={{ title: "Change Password" }}
      />
    </Stack.Navigator>
  );
}
